// src/components/RapportCaisse.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, onSnapshot, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { format, startOfDay, endOfDay, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Printer, FileSpreadsheet, FileText, Loader2, TrendingUp, TrendingDown, Wallet } from 'lucide-react';

// --- FONCTIONS UTILITAIRES ---

const getSafeDate = (timestamp) => {
    if (!timestamp) return new Date();
    if (timestamp.toDate && typeof timestamp.toDate === 'function') return timestamp.toDate();
    if (timestamp instanceof Date) return timestamp;
    const parsed = new Date(timestamp);
    return isValid(parsed) ? parsed : new Date();
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(amount);
};

const downloadCSV = (content, fileName) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const RapportCaisse = ({ db, schoolId }) => {
    // États
    const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    
    const [paiements, setPaiements] = useState([]);
    const [depenses, setDepenses] = useState([]);
    
    const [isLoadingPaiements, setIsLoadingPaiements] = useState(false);
    const [isLoadingDepenses, setIsLoadingDepenses] = useState(false);
    const [schoolData, setSchoolData] = useState(null);

    const isLoading = isLoadingPaiements || isLoadingDepenses;

    // --- 1. RÉCUPÉRATION DES INFOS DE L'ÉCOLE ---
    useEffect(() => {
        if (!db || !schoolId) return;
        const fetchSchoolData = async () => {
            try {
                const schoolRef = doc(db, 'schools', schoolId);
                const snap = await getDoc(schoolRef);
                if (snap.exists()) {
                    setSchoolData(snap.data());
                }
            } catch (err) {
                console.error("Erreur chargement école:", err);
            }
        };
        fetchSchoolData();
    }, [db, schoolId]);

    // --- 2. RÉCUPÉRATION DES DONNÉES (PAIEMENTS & DÉPENSES) ---
    useEffect(() => {
        if (!db || !schoolId) return;

        const start = startOfDay(parseISO(startDate) || new Date());
        const end = endOfDay(parseISO(endDate) || new Date());

        setIsLoadingPaiements(true);
        setIsLoadingDepenses(true);

        // A. Récupération des Paiements (ENTRÉES)
        const paiementsRef = collection(db, 'schools', schoolId, 'paiements');
        const qPaiements = query(
            paiementsRef,
            where('datePaiement', '>=', start),
            where('datePaiement', '<=', end),
            orderBy('datePaiement', 'asc')
        );

        const unsubscribePaiements = onSnapshot(qPaiements, (snapshot) => {
            const fetched = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    type: 'ENTREE',
                    rawDate: getSafeDate(data.datePaiement),
                    montant: parseFloat(data.montantPaye) || 0,
                    label: `Paiement: ${data.eleveName || 'Inconnu'}`,
                    detail: data.typePaiement === 'mensualite' ? `Mensualité (${data.moisConcerne})` : data.typePaiement,
                    moyen: data.moyenPaiement || 'Espèces',
                    reference: data.referenceTransaction || '-'
                };
            });
            setPaiements(fetched);
            setIsLoadingPaiements(false);
        }, (error) => {
            console.error("Erreur paiements:", error);
            setIsLoadingPaiements(false);
        });

        // B. Récupération des Dépenses (SORTIES)
        const depensesRef = collection(db, 'schools', schoolId, 'depenses');
        // Note: Assurez-vous que le champ date dans 'depenses' est bien nommé 'date' comme dans DepensesManagement.js
        const qDepenses = query(
            depensesRef,
            where('date', '>=', start),
            where('date', '<=', end),
            orderBy('date', 'asc')
        );

        const unsubscribeDepenses = onSnapshot(qDepenses, (snapshot) => {
            const fetched = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    type: 'SORTIE',
                    rawDate: getSafeDate(data.date),
                    montant: parseFloat(data.amount) || 0, // 'amount' dans DepensesManagement
                    label: `Dépense: ${data.category || 'Générale'}`,
                    detail: data.description,
                    moyen: 'Espèces/Autre', // Ajustez si vous gérez les moyens de paiement pour les dépenses
                    reference: '-'
                };
            });
            setDepenses(fetched);
            setIsLoadingDepenses(false);
        }, (error) => {
            console.error("Erreur dépenses:", error);
            setIsLoadingDepenses(false);
        });

        return () => {
            unsubscribePaiements();
            unsubscribeDepenses();
        };
    }, [db, schoolId, startDate, endDate]);

    // --- 3. CALCULS & FUSION ---

    // Fusionner et trier chronologiquement
    const combinedTransactions = useMemo(() => {
        const all = [...paiements, ...depenses];
        return all.sort((a, b) => a.rawDate - b.rawDate).map(t => ({
            ...t,
            dateFormatted: format(t.rawDate, 'dd/MM/yyyy HH:mm', { locale: fr }),
            dateOnly: format(t.rawDate, 'yyyy-MM-dd'),
            dateDisplay: format(t.rawDate, 'dd/MM/yyyy'),
            hourOnly: format(t.rawDate, 'HH:mm'),
        }));
    }, [paiements, depenses]);

    // Calculs par jour (Encaissement, Décaissement, Solde Jour)
    const dailyStats = useMemo(() => {
        const stats = {};
        combinedTransactions.forEach(t => {
            const dateKey = t.dateOnly;
            if (!stats[dateKey]) {
                stats[dateKey] = { date: t.rawDate, dateDisplay: t.dateDisplay, in: 0, out: 0, balance: 0 };
            }
            if (t.type === 'ENTREE') {
                stats[dateKey].in += t.montant;
                stats[dateKey].balance += t.montant;
            } else {
                stats[dateKey].out += t.montant;
                stats[dateKey].balance -= t.montant;
            }
        });
        return Object.values(stats).sort((a, b) => a.date - b.date);
    }, [combinedTransactions]);

    // Totaux globaux
    const globalStats = useMemo(() => {
        return combinedTransactions.reduce((acc, t) => {
            if (t.type === 'ENTREE') {
                acc.totalIn += t.montant;
                acc.balance += t.montant;
            } else {
                acc.totalOut += t.montant;
                acc.balance -= t.montant;
            }
            return acc;
        }, { totalIn: 0, totalOut: 0, balance: 0 });
    }, [combinedTransactions]);

    // --- 4. EXPORT & IMPRESSION ---

    const handleExportCSV = () => {
        const separator = ';';
        const headers = ["Date", "Heure", "Type", "Libellé", "Détail/Motif", "Moyen", "Entrée (Credit)", "Sortie (Debit)"];
        
        const rows = combinedTransactions.map(t => {
            const clean = (str) => `"${String(str || '').replace(/"/g, '""')}"`;
            return [
                clean(t.dateDisplay),
                clean(t.hourOnly),
                clean(t.type),
                clean(t.label),
                clean(t.detail),
                clean(t.moyen),
                t.type === 'ENTREE' ? t.montant : 0,
                t.type === 'SORTIE' ? t.montant : 0
            ].join(separator);
        });

        const csvContent = '\uFEFF' + [headers.join(separator), ...rows].join('\n');
        downloadCSV(csvContent, `Journal_Caisse_${startDate}_au_${endDate}.csv`);
    };

    const handlePrintWindow = () => {
        const schoolName = schoolData?.name || 'Nom de l\'École';
        const logoUrl = schoolData?.logoUrl || null;

        const logoHtml = logoUrl 
            ? `<img src="${logoUrl}" style="max-height: 80px; margin-bottom: 10px;" />` 
            : `<h2>${schoolName}</h2>`;

        const detailsRows = combinedTransactions.map(t => `
            <tr>
                <td>${t.dateDisplay} <small>${t.hourOnly}</small></td>
                <td>${t.label} <br/><small style="color:#666;">${t.detail}</small></td>
                <td>${t.moyen}</td>
                <td style="text-align: right; color: ${t.type === 'ENTREE' ? 'green' : '#ccc'};">
                    ${t.type === 'ENTREE' ? formatCurrency(t.montant) : '-'}
                </td>
                <td style="text-align: right; color: ${t.type === 'SORTIE' ? 'red' : '#ccc'};">
                    ${t.type === 'SORTIE' ? formatCurrency(t.montant) : '-'}
                </td>
            </tr>
        `).join('');

        const summaryRows = dailyStats.map(s => `
            <tr>
                <td>${format(s.date, 'EEEE dd MMMM', { locale: fr })}</td>
                <td style="text-align: right; color: green;">${formatCurrency(s.in)}</td>
                <td style="text-align: right; color: red;">${formatCurrency(s.out)}</td>
                <td style="text-align: right; font-weight: bold;">${formatCurrency(s.balance)}</td>
            </tr>
        `).join('');

        const htmlContent = `
        <html>
            <head>
                <title>Journal de Caisse</title>
                <style>
                    body { font-family: 'Segoe UI', sans-serif; padding: 20px; color: #333; font-size: 12px; }
                    .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #eee; padding-bottom: 10px; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                    th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
                    th { background-color: #f8f9fa; font-weight: bold; text-transform: uppercase; font-size: 11px; }
                    .total-row td { background-color: #f0f0f0; font-weight: bold; font-size: 13px; }
                    .kpi-container { display: flex; justify-content: space-between; margin-bottom: 20px; gap: 10px; }
                    .kpi-box { border: 1px solid #ddd; padding: 10px; flex: 1; text-align: center; border-radius: 4px; }
                    .kpi-title { font-size: 10px; text-transform: uppercase; color: #666; }
                    .kpi-value { font-size: 16px; font-weight: bold; margin-top: 5px; }
                </style>
            </head>
            <body>
                <div class="header">
                    ${logoHtml}
                    <h3>JOURNAL DE CAISSE</h3>
                    <p>Période du ${format(parseISO(startDate), 'dd/MM/yyyy')} au ${format(parseISO(endDate), 'dd/MM/yyyy')}</p>
                </div>

                <div class="kpi-container">
                    <div class="kpi-box" style="background-color: #f0fdf4; border-color: #bbf7d0;">
                        <div class="kpi-title">Total Encaissements</div>
                        <div class="kpi-value" style="color: #166534;">+ ${formatCurrency(globalStats.totalIn)}</div>
                    </div>
                    <div class="kpi-box" style="background-color: #fef2f2; border-color: #fecaca;">
                        <div class="kpi-title">Total Décaissements</div>
                        <div class="kpi-value" style="color: #991b1b;">- ${formatCurrency(globalStats.totalOut)}</div>
                    </div>
                    <div class="kpi-box" style="background-color: #eff6ff; border-color: #bfdbfe;">
                        <div class="kpi-title">Solde Période</div>
                        <div class="kpi-value" style="color: #1e40af;">= ${formatCurrency(globalStats.balance)}</div>
                    </div>
                </div>

                <h4>RÉSUMÉ JOURNALIER</h4>
                <table>
                    <thead>
                        <tr><th>Date</th><th style="text-align:right">Entrées</th><th style="text-align:right">Sorties</th><th style="text-align:right">Solde Jour</th></tr>
                    </thead>
                    <tbody>${summaryRows}</tbody>
                </table>

                <h4>DÉTAIL DES MOUVEMENTS</h4>
                <table>
                    <thead>
                        <tr><th>Date/Heure</th><th>Libellé</th><th>Moyen</th><th style="text-align:right">Recettes</th><th style="text-align:right">Dépenses</th></tr>
                    </thead>
                    <tbody>
                        ${detailsRows}
                        <tr class="total-row">
                            <td colspan="3" style="text-align: right;">TOTAUX</td>
                            <td style="text-align: right; color: green;">${formatCurrency(globalStats.totalIn)}</td>
                            <td style="text-align: right; color: red;">${formatCurrency(globalStats.totalOut)}</td>
                        </tr>
                    </tbody>
                </table>
                <p style="text-align: right; font-style: italic; font-size: 10px;">Généré le ${new Date().toLocaleString('fr-FR')}</p>
            </body>
        </html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(htmlContent);
            printWindow.document.close();
            printWindow.onload = () => printWindow.print();
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center mb-4 sm:mb-0">
                    <FileText className="mr-3 text-indigo-600" />
                    Rapport de Caisse & Trésorerie
                </h1>
            </div>

            {/* --- BARRE DE FILTRES --- */}
            <div className="bg-white shadow rounded-lg p-4 mb-6 flex flex-col lg:flex-row gap-4 items-end">
                <div className="w-full lg:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Du :</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="block w-full p-2 border rounded-md" />
                </div>
                <div className="w-full lg:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Au :</label>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="block w-full p-2 border rounded-md" />
                </div>
                <div className="flex gap-2 w-full lg:w-auto mt-2 lg:mt-0">
                    <button onClick={handlePrintWindow} className="flex-1 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                        <Printer size={18} className="mr-2" /> Imprimer
                    </button>
                    <button onClick={handleExportCSV} className="flex-1 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                        <FileSpreadsheet size={18} className="mr-2" /> Excel
                    </button>
                </div>
            </div>

            {/* --- KPIS (CARTES DE TOTAUX) --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-sm text-green-600 font-medium uppercase">Total Entrées</p>
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(globalStats.totalIn)}</p>
                    </div>
                    <TrendingUp className="text-green-400 h-10 w-10" />
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-sm text-red-600 font-medium uppercase">Total Sorties</p>
                        <p className="text-2xl font-bold text-red-700">{formatCurrency(globalStats.totalOut)}</p>
                    </div>
                    <TrendingDown className="text-red-400 h-10 w-10" />
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-sm text-blue-600 font-medium uppercase">Solde Période</p>
                        <p className={`text-2xl font-bold ${globalStats.balance >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                            {formatCurrency(globalStats.balance)}
                        </p>
                    </div>
                    <Wallet className="text-blue-400 h-10 w-10" />
                </div>
            </div>

            {/* --- CONTENU PRINCIPAL --- */}
            <div className="bg-white shadow rounded-lg p-4">
                {isLoading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="animate-spin h-10 w-10 text-indigo-600" />
                        <span className="ml-3 text-gray-500">Chargement des transactions...</span>
                    </div>
                ) : (
                    <>
                        {/* 1. RÉSUMÉ JOURNALIER */}
                        <div className="mb-8">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Résumé Journalier</h3>
                            <div className="overflow-x-auto border rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-green-600 uppercase">Encaissements</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-red-600 uppercase">Décaissements</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">Solde Jour</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {dailyStats.map((stat, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {format(stat.date, 'EEEE dd MMMM', { locale: fr })}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-right font-medium text-green-600">
                                                    +{formatCurrency(stat.in)}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-right font-medium text-red-600">
                                                    -{formatCurrency(stat.out)}
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-right font-bold text-gray-800">
                                                    {formatCurrency(stat.balance)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* 2. DÉTAIL DES TRANSACTIONS */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Détail des Transactions (Brouillard)</h3>
                            <div className="overflow-x-auto border rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Heure</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Libellé</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Détail</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {combinedTransactions.map((t) => (
                                            <tr key={`${t.type}-${t.id}`} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {t.dateDisplay} <span className="text-xs text-gray-400 ml-1">{t.hourOnly}</span>
                                                </td>
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{t.label}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-xs" title={t.detail}>
                                                    {t.detail}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        t.type === 'ENTREE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                    }`}>
                                                        {t.type === 'ENTREE' ? 'Entrée' : 'Sortie'}
                                                    </span>
                                                </td>
                                                <td className={`px-4 py-3 text-right text-sm font-bold ${
                                                    t.type === 'ENTREE' ? 'text-green-600' : 'text-red-600'
                                                }`}>
                                                    {t.type === 'ENTREE' ? '+' : '-'}{formatCurrency(t.montant)}
                                                </td>
                                            </tr>
                                        ))}
                                        {combinedTransactions.length === 0 && (
                                            <tr>
                                                <td colspan="5" className="px-6 py-10 text-center text-gray-500 italic">
                                                    Aucune transaction (paiement ou dépense) trouvée pour cette période.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RapportCaisse;